import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useMobileChat } from '../../context/MobileChatContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  MessageSquare,
  ArrowLeft,
  Send,
  Users,
  Briefcase,
  Loader2,
  Home
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { formatTimeFromDate } from '../../utils/dateFormatting';

const MobileChat = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { operator, tenantSettings } = useMobileAuth();
  const {
    rooms,
    activeRoom,
    messages,
    loading,
    openRoom,
    closeChat,
    sendMessage,
    fetchRooms
  } = useMobileChat();

  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Open room if roomId provided
  useEffect(() => {
    if (roomId && rooms.length > 0) {
      const room = rooms.find(r => r.id === roomId);
      if (room && (!activeRoom || activeRoom.id !== roomId)) {
        openRoom(room);
      }
    }
  }, [roomId, rooms, activeRoom, openRoom]);

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleSend = async () => {
    if (!messageInput.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(messageInput.trim());
      setMessageInput('');
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBack = () => {
    if (activeRoom) {
      closeChat();
      navigate(-1);
    } else {
      navigate('/mobile/dashboard');
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return formatTimeFromDate(date, tenantSettings.time_format);
    } else if (diffDays === 1) {
      return 'Yesterday ' + formatTimeFromDate(date, tenantSettings.time_format);
    }
    return format(date, 'dd/MM') + ' ' + formatTimeFromDate(date, tenantSettings.time_format);
  };

  // Chat Room View
  if (activeRoom) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex flex-col">
        {/* Header */}
        <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center gap-3 sticky top-0 z-10 safe-top">
          <button onClick={handleBack} className="text-[var(--m-text-secondary)] p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-[var(--m-text)] font-semibold truncate">{activeRoom.name}</h1>
            <p className="text-[var(--m-text-secondary)] text-xs">
              {activeRoom.participants?.length || 0} participants
            </p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-[var(--m-text-muted)] py-8">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No messages yet</p>
              <p className="text-sm">Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, idx) => {
                const isOwn = msg.sender_id === operator?.id;
                const isSystem = msg.type === 'system';

                if (isSystem) {
                  return (
                    <div key={msg.id || idx} className="text-center text-xs text-[var(--m-text-muted)] py-1">
                      {msg.content}
                    </div>
                  );
                }

                return (
                  <div
                    key={msg.id || idx}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 ${
                        isOwn
                          ? 'bg-[#ffd108] text-black'
                          : 'bg-[var(--m-bg-card)] text-[var(--m-text)]'
                      }`}
                    >
                      {!isOwn && (
                        <p className="text-xs font-medium mb-1 text-[#ffd108]">
                          {msg.sender_name}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>
                      <p className={`text-[10px] mt-1 ${isOwn ? 'text-black/60' : 'text-[var(--m-text-muted)]'}`}>
                        {formatMessageTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 bg-[var(--m-bg-card)] border-t border-[var(--m-border)]">
          <div className="flex gap-2">
            <Input
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              disabled={sending}
              className="flex-1 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] placeholder:text-[var(--m-text-muted)]"
            />
            <Button
              onClick={handleSend}
              disabled={!messageInput.trim() || sending}
              className="bg-[#ffd108] hover:bg-[#e5bc07] text-black"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Parse room name into title and site
  const parseRoomName = (name) => {
    if (!name) return { title: 'Unknown', site: null };
    // Format: "Job #25 - Customer Name - Site Name"
    const parts = name.split(' - ');
    if (parts.length >= 3) {
      // Job # and Customer on first line, Site on second
      return { 
        title: `${parts[0]} - ${parts[1]}`, 
        site: parts.slice(2).join(' - ') 
      };
    } else if (parts.length === 2) {
      return { title: parts[0], site: parts[1] };
    }
    return { title: name, site: null };
  };

  // Room List View
  return (
    <div className="min-h-screen bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center justify-between sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="text-[var(--m-text-secondary)] p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-[#ffd108]" />
            <h1 className="text-[var(--m-text)] font-semibold">Messages</h1>
          </div>
        </div>
        <button 
          onClick={() => navigate('/mobile/dashboard')} 
          className="text-[var(--m-text-secondary)] p-1"
          data-testid="home-btn"
        >
          <Home className="h-5 w-5" />
        </button>
      </div>

      {/* Room List */}
      <div className="p-4">
        {rooms.length === 0 ? (
          <div className="text-center text-[var(--m-text-muted)] py-12">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No conversations yet</p>
            <p className="text-sm mt-1">Chat rooms will appear here when you&apos;re added to one</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rooms.map((room) => {
              const { title, site } = parseRoomName(room.name);
              const hasUnread = room.unread_count > 0;
              
              return (
                <button
                  key={room.id}
                  onClick={() => openRoom(room)}
                  className={`w-full p-4 rounded-lg text-left transition-colors ${
                    hasUnread ? 'bg-[var(--m-bg-elevated)] border border-[#ffd108]/30' : 'bg-[var(--m-bg-card)] hover:bg-[var(--m-bg-elevated)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <div className={`p-2 rounded-full ${
                        room.room_type === 'job' ? 'bg-blue-500/20' : 'bg-[#ffd108]/20'
                      }`}>
                        {room.room_type === 'job' ? (
                          <Briefcase className="h-5 w-5 text-blue-400" />
                        ) : (
                          <Users className="h-5 w-5 text-[#ffd108]" />
                        )}
                      </div>
                      {hasUnread && (
                        <span className="absolute -top-1 -right-1 bg-[#ffd108] text-black text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                          {room.unread_count > 9 ? '9+' : room.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-medium truncate ${hasUnread ? 'text-[var(--m-text)]' : 'text-[var(--m-text)]'}`}>{title}</h3>
                        {room.last_message_at && (
                          <span className="text-xs text-[var(--m-text-muted)] ml-2 shrink-0">
                            {formatDistanceToNow(new Date(room.last_message_at), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                      {site && (
                        <p className="text-xs text-[var(--m-text-secondary)] truncate">{site}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${
                            room.room_type === 'job' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-[var(--m-bg-elevated)] text-[var(--m-text-body)]'
                          }`}
                        >
                          {room.room_type === 'job' ? 'Job Chat' : 'General'}
                        </Badge>
                        {hasUnread && (
                          <span className="text-xs text-[#ffd108] font-medium">New</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileChat;
